import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import uniqid from 'uniqid';

import Badge from 'components/shared/others/screenshot-annotator/components/badge';
import NotePanel from 'components/shared/others/screenshot-annotator/components/note-panel';
import CursorOverlay from 'components/shared/others/screenshot-annotator/components/cursor-overlay';
import useImageBox from 'components/shared/others/screenshot-annotator/hooks/use-image-box';

export interface AnnotationNote {
  id: string | number;
  xPct: number;
  yPct: number;
  text: string;
}

interface ActiveState {
  mode: 'add' | 'edit';
  xPct?: number;
  yPct?: number;
  id?: string | number;
}

interface ScreenshotAnnotatorProps {
  imageSrc: string | null;
  initialNotes?: AnnotationNote[];
  onChange?: (notes: AnnotationNote[]) => void;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const PANEL_WIDTH_PX = 450;
const PANEL_MARGIN_PX = 12;
const PANEL_HEIGHT_PX = 240;

const ScreenshotAnnotator = ({ imageSrc, initialNotes = [], onChange }: ScreenshotAnnotatorProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [notes, setNotes] = useState<AnnotationNote[]>(initialNotes);
  const [active, setActive] = useState<ActiveState | null>(null);
  const imageBox = useImageBox(imageRef, containerRef);
  const [isHovering, setIsHovering] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [hoveringPin, setHoveringPin] = useState(false);

  const isEditing = useMemo(() => active?.mode === 'edit', [active]);
  const isAdding = useMemo(() => active?.mode === 'add', [active]);

  useEffect(() => {
    if (onChange) onChange(notes);
  }, [notes, onChange]);

  const handleImageClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!imageRef.current) return;
      // Avoid clicks when panel is open
      if (active) return;

      const rect = imageRef.current.getBoundingClientRect();
      const xPct = clamp((e.clientX - rect.left) / rect.width, 0, 1);
      const yPct = clamp((e.clientY - rect.top) / rect.height, 0, 1);

      setActive({ xPct, yPct, mode: 'add' });
    },
    [active]
  );

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const cont = containerRef.current;

    if (!cont) return;

    const rect = cont.getBoundingClientRect();

    setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const insideImage = useMemo(
    () =>
      cursor.x >= imageBox.left &&
      cursor.x <= imageBox.left + imageBox.width &&
      cursor.y >= imageBox.top &&
      cursor.y <= imageBox.top + imageBox.height,
    [cursor, imageBox]
  );

  const handleSaveNew = (text: string) => {
    if (!active) return;

    const newNote: AnnotationNote = {
      id: uniqid(),
      xPct: active.xPct ?? 0,
      yPct: active.yPct ?? 0,
      text: text.trim(),
    };

    setNotes((prev) => [...prev, newNote]);
    setActive(null);
  };

  const handleSaveEdit = (text: string) => {
    const noteId = active?.id;

    setNotes((prev) =>
      prev.map((n) => (n.id === noteId ? { ...n, text: text.trim() } : n))
    );

    setActive(null);
  };

  const handleDelete = () => {
    const noteId = active?.id;

    // Clear active first to avoid transient access to a removed note during rerender
    setActive(null);

    setNotes((prev) => prev.filter((n) => n.id !== noteId));
  };

  const openExisting = (index: number) =>
    setActive({ mode: 'edit', id: notes[index]?.id });

  const panelPosition = useMemo(() => {
    if (!active) return null;

    const imgRect = imageRef.current?.getBoundingClientRect();

    if (!imgRect) return null;

    const pct = isAdding
      ? { x: active.xPct, y: active.yPct }
      : (() => {
          const note = notes.find((n) => n.id === active.id);

          return { x: note?.xPct, y: note?.yPct };
        })();

    if (!pct.x || !pct.y) return null;

    const anchorX = imgRect.left + pct.x * imgRect.width;
    const anchorY = imgRect.top + pct.y * imgRect.height;

    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;

    let leftPx = anchorX + PANEL_MARGIN_PX;

    leftPx = Math.min(
      Math.max(leftPx, PANEL_MARGIN_PX),
      Math.max(PANEL_MARGIN_PX, vw - PANEL_WIDTH_PX - PANEL_MARGIN_PX)
    );

    let topPx = anchorY + PANEL_MARGIN_PX;

    if (topPx + PANEL_HEIGHT_PX > vh - PANEL_MARGIN_PX) {
      topPx = Math.max(
        PANEL_MARGIN_PX,
        anchorY - PANEL_HEIGHT_PX - PANEL_MARGIN_PX
      );
    }

    return {
      left: `${leftPx}px`,
      top: `${topPx}px`,
    };
  }, [active, notes]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full flex justify-center items-center"
      onMouseMove={handleMouseMove} // NOSONAR
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      role="presentation"
    >
      <button
        type="button"
        className={classNames(
          `rounded select-none p-0 border-0 bg-transparent`,
          {
            'cursor-default': active || (!active && !isHovering && hoveringPin),
            'cursor-none': !active && isHovering && insideImage && !hoveringPin,
          }
        )}
        onClick={handleImageClick}
      >
        <img
          ref={imageRef}
          src={imageSrc ?? undefined}
          alt="Screenshot"
          className="rounded"
          draggable={false}
        />
      </button>

      <CursorOverlay
        visible={!active && isHovering && insideImage && !hoveringPin}
        x={cursor.x}
        y={cursor.y}
      />

      {notes.map((note, index) => (
        <button
          key={note.id}
          type="button"
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${imageBox.left + note.xPct * imageBox.width}px`,
            top: `${imageBox.top + note.yPct * imageBox.height}px`,
          }}
          onClick={(e) => {
            e.stopPropagation();
            openExisting(index);
          }}
          onMouseEnter={() => setHoveringPin(true)}
          onMouseLeave={() => setHoveringPin(false)}
        >
          <Badge index={index + 1} />
        </button>
      ))}

      {active &&
        panelPosition &&
        ReactDOM.createPortal(
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <div
            className="fixed z-[10000]"
            role="dialog"
            style={{ left: panelPosition.left, top: panelPosition.top }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <NotePanel
              initialText={
                isEditing
                  ? notes.find((n) => n.id === active.id)?.text || ''
                  : ''
              }
              mode={active.mode}
              onSave={isEditing ? handleSaveEdit : handleSaveNew}
              onCancel={() => setActive(null)}
              onDelete={isEditing ? handleDelete : undefined}
            />
          </div>,
          document.getElementById('modal-overlay') || document.body
        )}
    </div>
  );
};

export default ScreenshotAnnotator;
